// Wraps a promise-returning fetcher with a hard timeout and a single
// auto-retry. Without this safety net, a Supabase query that never
// resolves (intermittent supabase-js / GoTrueClient state issue we've
// seen after some SPA navigations) would leave the page sitting on
// "Loading…" indefinitely. With it, the user either gets data or an
// actionable error within ~25 seconds.
//
//   const rows = await loadWithRetry(fetchAllPublished, { label: 'teaching' });
//
// timeoutMs   how long ONE attempt is allowed to hang before we abort it
// attempts    total tries (1 + retries); 2 means "try once, retry once"
// label       short identifier surfaced in console.warn when the timeout
//             fires — handy when we only know "some page hung" and want
//             to figure out which fetch was the culprit.

export async function loadWithRetry(fetcher, opts = {}) {
  const { timeoutMs = 20_000, attempts = 2, label = 'fetch' } = opts;
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await withTimeout(fetcher(), timeoutMs, label, i + 1);
    } catch (err) {
      lastErr = err;
      // Small backoff before retrying so a transient network blip has a
      // moment to clear. Skip the sleep on the final attempt.
      if (i < attempts - 1) await sleep(500);
    }
  }
  throw lastErr ?? new Error('Unknown error');
}

function withTimeout(promise, ms, label, attempt) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(`[hc] ${label}: attempt ${attempt} timed out after ${ms}ms`);
      reject(new Error(`Request timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
